import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkle, ArrowRight, Ticket, ChartLineUp, ShieldCheck, QrCode } from "@phosphor-icons/react";
import { Logo } from "../components/shared";
import { Button } from "../components/ui/button";
import EventCard from "../components/EventCard";
import { PlayStoreLink } from "../components/PlayStoreLink";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { getSavedCity } from "../lib/useConfig";

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (user) {
      navigate(user.role === "admin" ? "/admin" : user.role === "organizer" ? "/organizer" : "/discover", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    api.get(`/events/discovery?city=${getSavedCity()}`).then((r) => {
      const merged = [...(r.data.featured || []), ...(r.data.trending || [])];
      const seen = new Set();
      const unique = merged.filter((e) => (seen.has(e.id) ? false : seen.add(e.id)));
      setEvents(unique.slice(0, 8));
    }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="relative z-20 mx-auto max-w-7xl px-4 md:px-6 h-20 flex items-center justify-between">
        <Logo imgClassName="h-14 w-auto" />
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" data-testid="landing-login"><Link to="/login">Log in</Link></Button>
          <Button asChild className="rounded-full" data-testid="landing-signup"><Link to="/login?mode=register">Get started</Link></Button>
        </div>
      </header>

      <section className="relative overflow-hidden grain">
        <div className="pointer-events-none absolute inset-y-0 right-0 w-full lg:w-[64%] hidden md:block overflow-hidden" data-testid="hero-image">
          <img src="/hero-collage.png" alt="Live events" className="ken-burns w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/40" />
          <div className="absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-background to-transparent" />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 md:px-6 pt-16 pb-24 lg:pb-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-5">
                <Sparkle weight="fill" size={14} /> AI-first events platform
              </span>
              <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]">
                Discover events<br />you'll actually <span className="text-primary">love.</span>
              </h1>
              <p className="mt-6 text-lg text-muted-foreground max-w-md">
                Ask AI to find the perfect show, book in seconds, and get a secure QR ticket. For organizers — create, sell and grow with an AI copilot.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-full h-12 px-7" data-testid="hero-explore">
                  <Link to="/login">Explore events <ArrowRight className="ml-2" /></Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7 border-foreground/20 bg-background/40 backdrop-blur" data-testid="hero-organizer">
                  <Link to="/become-organizer">Become an organizer</Link>
                </Button>
                <PlayStoreLink testId="landing-play-store-download" />
              </div>
              <div className="mt-10 flex flex-wrap gap-6 text-sm text-muted-foreground">
                {[[Ticket, "M-Tickets & QR"], [ChartLineUp, "AI analytics"], [ShieldCheck, "Secure KYC"], [QrCode, "Gate check-in"]].map(([Icon, l], i) => (
                  <span key={i} className="inline-flex items-center gap-2"><Icon size={18} className="text-primary" /> {l}</span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {events.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 md:px-6 pb-24">
          <div className="overline mb-1">Trending now</div>
          <h2 className="font-heading text-3xl font-semibold tracking-tight mb-6">Happening around you</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {events.slice(0, 8).map((e) => <EventCard key={e.id} event={e} />)}
          </div>
          <div className="mt-10 rounded-2xl brand-gradient text-white p-8 md:p-12 grain relative overflow-hidden shadow-xl shadow-primary/20">
            <div className="absolute right-0 top-0 w-64 h-64 rounded-full bg-white/10 blur-2xl" />
            <div className="relative z-[2] max-w-xl">
              <h3 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Ready to sell out your next event?</h3>
              <p className="text-white/80 mt-3">Onboard in minutes, publish with AI, and let the Organizer Copilot grow your sales.</p>
              <Button asChild size="lg" className="mt-6 rounded-full h-12 px-7 bg-white text-[#7C3AED] shadow hover:bg-white/90" data-testid="cta-organizer">
                <Link to="/become-organizer">Start organizing <ArrowRight className="ml-2" /></Link>
              </Button>
            </div>
          </div>
        </section>
      )}

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-10 md:flex-row md:items-center md:justify-between md:px-6">
          <div>
            <Logo />
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              Discover, book, and manage event tickets — all in one place.
            </p>
          </div>
          <PlayStoreLink testId="landing-play-store-footer" footer />
        </div>
      </footer>
    </div>
  );
}
